import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api.ts';
import ImageGallery from '../components/ImageGallery.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import { FileAttachment } from '../components/FileUpload.tsx';
import { formatCurrency, applyCurrencyMask, parseCurrency } from '../utils/currency.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';

// Função utilitária para construir URLs do backend
const getBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CubeIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PhotoIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// Types
interface Asset {
  id: string;
  description: string;
  type: 'real_estate' | 'vehicle' | 'other';
  acquisitionValue: number;
  amountPaid: number;
  currentValue: number;
  acquisitionDate: string;
  status: 'active' | 'sold' | 'exchanged' | 'rented';
  details: any;
  attachments?: FileAttachment[];
  parentAssetId?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AssetTransaction {
  id: string;
  type: 'sale' | 'exchange' | 'valuation_update' | 'status_change';
  description: string;
  amount?: number;
  date: string;
  relatedAssetId?: string;
  relatedAssetDescription?: string;
  details: any;
}

interface RelatedAsset {
  id: string;
  description: string;
  type: string;
  status: string;
  currentValue: number;
  relationship: 'parent' | 'child' | 'exchange_origin' | 'exchange_result';
}

interface Rental {
  id: string;
  assetId: number;
  tenantName: string;
  tenantEmail?: string;
  tenantPhone?: string;
  monthlyRent: number;
  paymentDay?: number | null;
  startDate: string;
  endDate?: string | null;
  status: 'active' | 'terminated';
  details?: any;
}

const AssetDetailNew: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<
    'details' | 'history' | 'related' | 'attachments' | 'debts'
  >('details');
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);
  const [deleteRentalModal, setDeleteRentalModal] = useState<{ isOpen: boolean; rentalId?: number; tenantName?: string }>({ isOpen: false });
  const [showDebtModal, setShowDebtModal] = useState(false);

  interface AssetDebt {
    id: string;
    description: string;
    amount: number;
    remainingAmount: number;
    interestRate: number;
    startDate?: string;
    dueDate?: string;
    status: 'active' | 'paid' | 'overdue' | 'renegotiated';
    creditor: string;
    assetId?: number;
  }

  interface AssetDebtFormData {
    creditorName: string;
    description: string;
    originalAmount: number;
    currentAmount: number;
    interestRate: number;
    dueDate: string;
    status: 'active' | 'paid' | 'overdue' | 'renegotiated';
    paymentMethod?: string;
    installments?: number;
    currentInstallment?: number;
  }

  const [debtForm, setDebtForm] = useState<AssetDebtFormData>({
    creditorName: '',
    description: '',
    originalAmount: 0,
    currentAmount: 0,
    interestRate: 0,
    dueDate: new Date().toISOString().split('T')[0],
    status: 'active',
    paymentMethod: '',
    installments: 1,
    currentInstallment: 1,
  });
  const [originalDebtAmountDisplay, setOriginalDebtAmountDisplay] = useState('');
  const [currentDebtAmountDisplay, setCurrentDebtAmountDisplay] = useState('');
  const [debtErrors, setDebtErrors] = useState<Record<string, string>>({});

  // Query para buscar dados do ativo
  const {
    data: asset,
    isLoading,
    error,
  } = useQuery<Asset>(['asset', id], async () => {
    const response = await api.get(`/assets/${id}`);
    console.log('🔍 AssetDetailNew - Dados do ativo recebidos:', response.data);

    // Processar attachments se existirem
    if (response.data.attachments && Array.isArray(response.data.attachments)) {
      response.data.attachments = response.data.attachments.map((attachment: any) => ({
        ...attachment,
        // Se for uma URL, usar como preview para imagens com URL completa
        preview:
          attachment.type === 'image' && attachment.url ? getBackendUrl(attachment.url) : undefined,
        // Criar um File object mock para compatibilidade
        file: new File([], attachment.name || 'file'),
      }));
    } else {
      response.data.attachments = [];
    }

    return response.data;
  });

  // Query para buscar histórico de transações
  const { data: transactions } = useQuery<AssetTransaction[]>(
    ['asset-transactions', id],
    async () => {
      const response = await api.get(`/assets/${id}/transactions`);
      return response.data;
    }
  );

  // Query para buscar ativos relacionados
  const { data: relatedAssets } = useQuery<RelatedAsset[]>(['related-assets', id], async () => {
    const response = await api.get(`/assets/${id}/related`);
    return response.data;
  });

  // Query para buscar aluguéis do ativo (somente para imóveis)
  const { data: rentalsByAsset } = useQuery<Rental[]>(
    ['assetRentals', id],
    async () => {
      const response = await api.get(`/assets/${id}/rentals`);
      return response.data;
    },
    { enabled: !!id && asset?.type === 'real_estate' }
  );

  const { data: assetDebts } = useQuery<AssetDebt[]>(
    ['assetDebts', id],
    async () => {
      const response = await api.get(`/assets/${id}/debts`);
      return response.data;
    },
    { enabled: !!id && asset?.type === 'real_estate' }
  );

  const deleteRentalMutation = useMutation(
    async (rentalId: number) => {
      const response = await api.delete(`/rentals/${rentalId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assetRentals', id]);
      },
      onError: (error: any) => {
        console.error('Erro ao remover aluguel:', error);
      },
    }
  );

  const createDebtForAssetMutation = useMutation(
    async (payload: AssetDebtFormData) => {
      const response = await api.post(`/assets/${id}/debts`, {
        creditorName: payload.creditorName,
        description: payload.description,
        originalAmount: payload.originalAmount,
        currentAmount: payload.currentAmount,
        interestRate: payload.interestRate,
        dueDate: payload.dueDate,
        status: payload.status,
        paymentMethod: payload.paymentMethod,
        installments: payload.installments,
        currentInstallment: payload.currentInstallment,
        category: 'real_estate',
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assetDebts', id]);
        queryClient.invalidateQueries('debts');
        setDebtForm({
          creditorName: '',
          description: '',
          originalAmount: 0,
          currentAmount: 0,
          interestRate: 0,
          dueDate: new Date().toISOString().split('T')[0],
          status: 'active',
          paymentMethod: '',
          installments: 1,
          currentInstallment: 1,
        });
        setOriginalDebtAmountDisplay('');
        setCurrentDebtAmountDisplay('');
        setDebtErrors({});
        setShowDebtModal(false);
      },
      onError: (error: any) => {
        console.error('Erro ao criar dívida para o imóvel:', error);
        setDebtErrors(prev => ({
          ...prev,
          submit: 'Erro ao criar dívida. Tente novamente.',
        }));
      },
    }
  );

  // Mutation para atualizar status
  const updateStatusMutation = useMutation(
    async (status: string) => {
      const response = await api.patch(`/assets/${id}/status`, { status });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['asset', id]);
        queryClient.invalidateQueries(['asset-transactions', id]);
        setShowStatusModal(false);
      },
    }
  );

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'real_estate':
        return <BuildingOfficeIcon className="h-6 w-6" />;
      case 'vehicle':
        return <TruckIcon className="h-6 w-6" />;
      default:
        return <CubeIcon className="h-6 w-6" />;
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'sale':
        return <CurrencyDollarIcon className="h-5 w-5 text-green-600" />;
      case 'exchange':
        return <ArrowRightIcon className="h-5 w-5 text-purple-600" />;
      case 'valuation_update':
        return <DocumentTextIcon className="h-5 w-5 text-primary-600" />;
      case 'status_change':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const handleStatusChange = (status: string) => {
    setNewStatus(status);
    setShowStatusModal(true);
  };

  const confirmStatusChange = () => {
    updateStatusMutation.mutate(newStatus);
  };

  const handleDebtInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setDebtForm(prev => ({
      ...prev,
      [name]:
        name === 'interestRate' || name === 'installments' || name === 'currentInstallment'
          ? Number(value)
          : value,
    }));
    if (debtErrors[name]) {
      setDebtErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDebtCurrencyChange = (
    field: 'originalAmount' | 'currentAmount',
    value: string
  ) => {
    const maskedValue = applyCurrencyMask(value);
    const numericValue = parseCurrency(maskedValue);

    if (field === 'originalAmount') {
      setOriginalDebtAmountDisplay(maskedValue);
      setDebtForm(prev => ({ ...prev, originalAmount: numericValue }));
      if (debtForm.currentAmount === 0) {
        setCurrentDebtAmountDisplay(maskedValue);
        setDebtForm(prev => ({ ...prev, currentAmount: numericValue }));
      }
    } else {
      setCurrentDebtAmountDisplay(maskedValue);
      setDebtForm(prev => ({ ...prev, currentAmount: numericValue }));
    }

    if (debtErrors[field]) {
      setDebtErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateDebtForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!debtForm.creditorName.trim()) {
      newErrors.creditorName = 'Nome do credor é obrigatório';
    }
    if (!debtForm.originalAmount || debtForm.originalAmount <= 0) {
      newErrors.originalAmount = 'Valor original deve ser maior que zero';
    }
    if (debtForm.currentAmount < 0) {
      newErrors.currentAmount = 'Saldo devedor não pode ser negativo';
    }
    if (!debtForm.dueDate) {
      newErrors.dueDate = 'Data de vencimento é obrigatória';
    }
    setDebtErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitDebt = (e: React.FormEvent) => {
    e.preventDefault();
    if (!asset || asset.type !== 'real_estate') {
      setDebtErrors(prev => ({
        ...prev,
        submit: 'Dívidas só podem ser cadastradas para imóveis',
      }));
      return;
    }
    if (!validateDebtForm()) {
      return;
    }
    createDebtForAssetMutation.mutate(debtForm);
  };

  const formatDateOnly = (s?: string) => {
    if (!s) return '-';
    const parts = String(s).split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return s;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Ativo não encontrado</h2>
          <p className="text-gray-600 mb-4">O ativo solicitado não foi encontrado.</p>
          <button
            onClick={() => navigate('/assets')}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Voltar para Ativos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/assets')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Ativos
          </button>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100 text-primary-600 mr-4">
                {getTypeIcon(asset.type)}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{asset.description}</h1>
                <div className="flex items-center mt-2 space-x-4">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(asset.status)}`}
                  >
                    {formatStatus(asset.status)}
                  </span>
                  <span className="text-gray-500">ID: {asset.id}</span>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => navigate(`/assets/${id}/edit`)}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Editar
              </button>

              <div className="relative">
                <select
                  value={asset.status}
                  onChange={e => handleStatusChange(e.target.value)}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors appearance-none pr-8"
                  disabled={asset.status !== 'active'}
                >
                  <option value="active">Ativo</option>
                  {asset.status === 'active' && <option value="inactive">Inativo</option>}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Detalhes
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'history'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Histórico
              </button>
              <button
                onClick={() => setActiveTab('related')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'related'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Ativos Relacionados
              </button>
              <button
                onClick={() => setActiveTab('attachments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'attachments'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <PhotoIcon className="h-4 w-4 mr-2 inline" />
                Anexos
              </button>
              {asset.type === 'real_estate' && (
                <button
                  onClick={() => setActiveTab('debts')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'debts'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Dívidas / Custos
                </button>
              )}
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'details' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Informações Principais */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  Informações Financeiras
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor de Aquisição
                    </label>
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-lg font-semibold text-gray-900">
                        {formatCurrency(asset.acquisitionValue)}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor Pago (Investido)
                    </label>
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-lg font-semibold text-blue-600">
                        {asset.amountPaid ? formatCurrency(asset.amountPaid) : formatCurrency(0)}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor Pendente
                    </label>
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-lg font-semibold text-orange-600">
                        {formatCurrency(asset.acquisitionValue - (asset.amountPaid || 0))}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valor Atual
                    </label>
                    <div className="flex items-center">
                      <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-lg font-semibold text-green-600">
                        {asset.currentValue ? formatCurrency(asset.currentValue) : '-'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ROI (Retorno sobre Investimento)
                    </label>
                    <div className="flex items-center">
                      {(asset.amountPaid || 0) > 0 ? (
                        ((asset.currentValue - asset.acquisitionValue) / asset.amountPaid) * 100 > 0 ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                        ) : (
                          <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                        )
                      ) : (
                        <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      )}
                      <span
                        className={`text-lg font-semibold ${
                          (asset.amountPaid || 0) > 0
                            ? ((asset.currentValue - asset.acquisitionValue) / asset.amountPaid) * 100 > 0
                              ? 'text-green-600'
                              : 'text-red-600'
                            : 'text-gray-900'
                        }`}
                      >
                        {(asset.amountPaid || 0) > 0
                          ? `${(((asset.currentValue - asset.acquisitionValue) / asset.amountPaid) * 100).toFixed(2)}%`
                          : '-'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data de Aquisição
                    </label>
                    <div className="flex items-center">
                      <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                      <span className="text-gray-900">
                        {new Date(asset.acquisitionDate).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valorização (Sobre Valor de Aquisição)
                    </label>
                    <div className="flex items-center">
                      {asset.currentValue > asset.acquisitionValue ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                      )}
                      <span
                        className={`font-semibold ${
                          asset.currentValue > asset.acquisitionValue
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {(
                          ((asset.currentValue - asset.acquisitionValue) / asset.acquisitionValue) *
                          100
                        ).toFixed(2)}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalhes Específicos */}
              {asset.type === 'real_estate' && asset.details && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <BuildingOfficeIcon className="h-6 w-6 mr-2 text-primary-600" />
                    Detalhes do Imóvel
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {asset.details.address && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Endereço
                        </label>
                        <p className="text-gray-900">{asset.details.address}</p>
                      </div>
                    )}

                    {asset.details.area && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Área</label>
                        <p className="text-gray-900">{asset.details.area} m²</p>
                      </div>
                    )}

                    {asset.details.registrationNumber && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Matrícula
                        </label>
                        <p className="text-gray-900">{asset.details.registrationNumber}</p>
                      </div>
                    )}

                    {asset.details.rooms && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Quartos
                        </label>
                        <p className="text-gray-900">{asset.details.rooms}</p>
                      </div>
                    )}

                    {asset.details.bathrooms && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Banheiros
                        </label>
                        <p className="text-gray-900">{asset.details.bathrooms}</p>
                      </div>
                    )}

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Características
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {asset.details.garage && (
                          <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-sm">
                            Garagem
                          </span>
                        )}
                        {asset.details.furnished && (
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm">
                            Mobiliado
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Detalhes Específicos para Veículos */}
              {asset.type === 'vehicle' && asset.details && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <TruckIcon className="h-6 w-6 mr-2 text-primary-600" />
                    Detalhes do Veículo
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Marca</label>
                      <p className="text-gray-900">{asset.details.brand}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Modelo</label>
                      <p className="text-gray-900">{asset.details.model}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ano</label>
                      <p className="text-gray-900">{asset.details.year}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Placa</label>
                      <p className="text-gray-900">{asset.details.licensePlate}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cor</label>
                      <p className="text-gray-900">{asset.details.color}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quilometragem
                      </label>
                      <p className="text-gray-900">
                        {asset.details.mileage?.toLocaleString('pt-BR')} km
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Observações */}
              {asset.type === 'real_estate' && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">Aluguéis</h3>
                    {asset.status === 'active' && (
                      <button
                        onClick={() => navigate(`/assets/${id}/rentals/new`)}
                        className="bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center"
                      >
                        <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                        Registrar Aluguel
                      </button>
                    )}
                  </div>

                  {rentalsByAsset && rentalsByAsset.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {rentalsByAsset.map(r => (
                        <div key={r.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900">{r.tenantName}</h4>
                              <p className="text-sm text-gray-600 mt-1">{formatCurrency(r.monthlyRent)}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(r.status)}`}>{formatStatus(r.status)}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                            <div>
                              <p className="text-gray-500">Dia de Pagamento</p>
                              <p className="text-gray-900">{r.paymentDay ?? '-'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Início</p>
                              <p className="text-gray-900">{formatDateOnly(r.startDate)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Fim</p>
                              <p className="text-gray-900">{formatDateOnly(r.endDate || undefined)}</p>
                            </div>
                          </div>
                          <div className="flex justify-end gap-3 mt-4">
                            <button onClick={() => navigate(`/rentals/${r.id}/edit`)} className="text-primary-600 hover:text-primary-800 text-sm font-medium">Editar</button>
                            <button onClick={() => setDeleteRentalModal({ isOpen: true, rentalId: Number(r.id), tenantName: r.tenantName })} className="text-red-600 hover:text-red-800 text-sm font-medium">Remover</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-gray-600">Nenhum aluguel registrado para este imóvel.</p>
                      <div className="mt-4">
                        {asset.status === 'active' && (
                          <button
                            onClick={() => navigate(`/assets/${id}/rentals/new`)}
                            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                          >
                            Registrar Aluguel
                          </button>
                        )}
                        </div>
                      </div>
                  )}
                </div>
              )}

              <ConfirmationModal
                isOpen={deleteRentalModal.isOpen}
                onClose={() => setDeleteRentalModal({ isOpen: false })}
                onConfirm={() => {
                  if (deleteRentalModal.rentalId) {
                    deleteRentalMutation.mutate(deleteRentalModal.rentalId, {
                      onSuccess: () => setDeleteRentalModal({ isOpen: false }),
                    });
                  }
                }}
                title="Remover Aluguel"
                message={`Tem certeza que deseja remover o aluguel de ${deleteRentalModal.tenantName || ''}? Esta ação não pode ser desfeita.`}
                confirmText="Remover"
                cancelText="Cancelar"
                type="danger"
                isLoading={deleteRentalMutation.isLoading}
              />
              {asset.details?.notes && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Observações</h3>
                  <p className="text-gray-700 leading-relaxed">{asset.details.notes}</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações do Sistema</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Criado em
                    </label>
                    <p className="text-gray-900">
                      {new Date(asset.createdAt).toLocaleDateString('pt-BR')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Última atualização
                    </label>
                    <p className="text-gray-900">
                      {new Date(asset.updatedAt).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>

                <div className="space-y-3">
                  <button
                    onClick={() => navigate(`/assets/${id}/edit`)}
                    className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Editar Ativo
                  </button>

                  {asset?.type === 'real_estate' && asset?.status === 'active' && (
                    <button
                      onClick={() => navigate(`/assets/${id}/rentals/new`)}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                    >
                      <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                      Registrar Aluguel
                    </button>
                  )}

                  {asset?.status === 'active' && (
                    <>
                      <button
                        onClick={() => navigate(`/exchanges/new?originType=asset&originId=${id}`)}
                        className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
                      >
                        <ArrowRightIcon className="h-4 w-4 mr-2" />
                        Registrar Permuta
                      </button>

                      <button
                        onClick={() => navigate(`/assets/${id}/sale`)}
                        className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                      >
                        <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                        Registrar Venda
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Histórico de Transações</h3>

            {transactions && transactions.length > 0 ? (
              <div className="space-y-4">
                {transactions.map(transaction => (
                  <div key={transaction.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start">
                        <div className="p-2 rounded-full bg-gray-100 mr-4">
                          {getTransactionIcon(transaction.type)}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{transaction.description}</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {new Date(transaction.date).toLocaleDateString('pt-BR')}
                          </p>
                          {transaction.details && (
                            <div className="mt-2 text-sm text-gray-700">
                              {transaction.type === 'valuation_update' && (
                                <p>
                                  Valor anterior:{' '}
                                  {formatCurrency(transaction.details.previousValue || 0)} → Novo
                                  valor: {formatCurrency(transaction.details.newValue || 0)}
                                </p>
                              )}
                              {transaction.details.reason && (
                                <p className="mt-1 italic">{transaction.details.reason}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {transaction.amount && (
                        <div className="text-right">
                          <span className="font-semibold text-green-600">
                            {formatCurrency(transaction.amount)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Nenhuma transação registrada ainda.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'related' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Ativos Relacionados</h3>

            {relatedAssets && relatedAssets.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {relatedAssets.map(relatedAsset => {
                  const getRelationshipLabel = () => {
                    if (relatedAsset.relationship === 'exchange_origin') return 'Cedidos na permuta';
                    if (relatedAsset.relationship === 'exchange_result') return 'Recebidos na permuta';
                    return 'Relacionado';
                  };

                  const getTypeLabel = () => {
                    if (relatedAsset.type === 'vehicle') return 'Veículo';
                    if (relatedAsset.type === 'aircraft') return 'Aeronave';
                    if (relatedAsset.type === 'vessel') return 'Embarcação';
                    if (relatedAsset.type === 'real_estate') return 'Imóvel';
                    return 'Patrimônio';
                  };

                  return (
                    <div key={relatedAsset.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{relatedAsset.description}</h4>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(relatedAsset.status)}`}
                        >
                          {formatStatus(relatedAsset.status)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">Tipo: {getTypeLabel()}</p>
                      <p className="text-sm text-gray-600 mb-2">
                        Valor: {formatCurrency(relatedAsset.currentValue)}
                      </p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                          Relacionamento: {getRelationshipLabel()}
                        </p>
                        <button
                          onClick={() => {
                            if (relatedAsset.type === 'vehicle') navigate(`/vehicles/${relatedAsset.id}`);
                            else if (relatedAsset.type === 'aircraft') navigate(`/aircrafts/${relatedAsset.id}`);
                            else if (relatedAsset.type === 'vessel') navigate(`/vessels/${relatedAsset.id}`);
                            else navigate(`/assets/${relatedAsset.id}`);
                          }}
                          className="text-primary-600 hover:text-primary-800 text-sm font-medium flex items-center"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Ver
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <CubeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Nenhum ativo relacionado encontrado.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'attachments' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Anexos - Fotos e Documentos</h3>
              <span className="text-sm text-gray-500">
                {asset.attachments?.length || 0} arquivo(s)
              </span>
            </div>

            {asset.attachments && asset.attachments.length > 0 ? (
              <div className="space-y-6">
                {/* Fotos */}
                {asset.attachments.filter(att => att.type === 'image').length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <PhotoIcon className="h-5 w-5 mr-2" />
                      Fotos ({asset.attachments.filter(att => att.type === 'image').length})
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {asset.attachments
                        .filter(att => att.type === 'image')
                        .map((attachment, index) => (
                          <div key={attachment.id} className="relative group">
                            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                              <img
                                src={
                                  attachment.url
                                    ? getBackendUrl(attachment.url)
                                    : attachment.preview
                                }
                                alt={attachment.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              />
                            </div>
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
                              <button
                                onClick={() => {
                                  setGalleryStartIndex(index);
                                  setShowImageGallery(true);
                                }}
                                className="opacity-0 group-hover:opacity-100 bg-white text-gray-900 p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
                              >
                                <EyeIcon className="h-5 w-5" />
                              </button>
                            </div>
                            <p className="mt-2 text-sm text-gray-600 truncate">{attachment.name}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Documentos */}
                {asset.attachments.filter(att => att.type === 'document').length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Documentos ({asset.attachments.filter(att => att.type === 'document').length})
                    </h4>
                    <div className="space-y-3">
                      {asset.attachments
                        .filter(att => att.type === 'document')
                        .map(attachment => (
                          <div
                            key={attachment.id}
                            className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-8 w-8 text-gray-400 mr-3" />
                              <div>
                                <p className="font-medium text-gray-900">{attachment.name}</p>
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  <span>{(attachment.size / 1024).toFixed(0)} KB</span>
                                  {attachment.category && (
                                    <span className="px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-xs">
                                      {attachment.category === 'contrato' && 'Contrato'}
                                      {attachment.category === 'escritura' && 'Escritura'}
                                      {attachment.category === 'projeto_arquitetura' &&
                                        'Projeto Arquitetura'}
                                      {attachment.category === 'projeto_engenharia' &&
                                        'Projeto Engenharia'}
                                      {attachment.category === 'outros' && 'Outros'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                // Download do arquivo real
                                if (attachment.url) {
                                  const link = document.createElement('a');
                                  link.href = getBackendUrl(attachment.url);
                                  link.download = attachment.name;
                                  link.target = '_blank';
                                  link.click();
                                } else if (attachment.file) {
                                  // Fallback para File objects
                                  const link = document.createElement('a');
                                  link.href = URL.createObjectURL(attachment.file);
                                  link.download = attachment.name;
                                  link.click();
                                }
                              }}
                              className="flex items-center px-3 py-2 text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded-lg transition-colors"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                              Download
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <PhotoIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">Nenhum anexo encontrado</h4>
                <p className="text-gray-500 mb-6">
                  Este ativo ainda não possui fotos ou documentos anexados.
                </p>
                <button
                  onClick={() => navigate(`/assets/${id}/edit`)}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Adicionar Anexos
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'debts' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Dívidas / Custos do Imóvel</h3>
                  <p className="text-sm text-gray-500">
                    {assetDebts ? assetDebts.length : 0} registro(s)
                  </p>
                </div>
                <button
                  onClick={() => setShowDebtModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Registrar Dívida / Custo
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {(() => {
                  const debts = assetDebts || [];
                  const totalAmount = debts.reduce(
                    (sum, d) => sum + (d.amount || 0),
                    0
                  );
                  const totalRemaining = debts.reduce(
                    (sum, d) => sum + (d.remainingAmount || 0),
                    0
                  );
                  const totalPaid = totalAmount - totalRemaining;
                  return (
                    <>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          Valor Total das Dívidas
                        </p>
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(totalAmount)}
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-green-700 mb-1">
                          Total Pago
                        </p>
                        <p className="text-lg font-bold text-green-800">
                          {formatCurrency(totalPaid)}
                        </p>
                      </div>
                      <div className="bg-orange-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-orange-700 mb-1">
                          Total a Pagar
                        </p>
                        <p className="text-lg font-bold text-orange-800">
                          {formatCurrency(totalRemaining)}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>

              {assetDebts && assetDebts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {assetDebts.map(debt => (
                    <button
                      type="button"
                      key={debt.id}
                      onClick={() => navigate(`/debts/${debt.id}`)}
                      className="text-left border border-gray-200 rounded-xl p-4 bg-white shadow-sm cursor-pointer hover:border-primary-200 hover:shadow-md transition w-full"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">
                            {debt.description || 'Dívida'}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">{debt.creditor}</p>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(debt.status)}`}
                        >
                          {formatStatus(debt.status)}
                        </span>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Valor Original</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(debt.amount)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Valor Restante</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(debt.remainingAmount)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Taxa de Juros</span>
                          <span className="font-semibold text-gray-900">
                            {debt.interestRate}% a.m.
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Vencimento</span>
                          <span
                            className={`font-semibold ${
                              debt.status === 'overdue' ? 'text-red-600' : 'text-gray-900'
                            }`}
                          >
                            {debt.dueDate ? formatDateOnly(debt.dueDate) : '-'}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <CurrencyDollarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium">
                    Nenhuma dívida cadastrada para este imóvel.
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    Clique no botão Registrar Dívida / Custo para adicionar financiamentos ou custos
                    relevantes.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal de Confirmação de Status */}
        {showStatusModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirmar Alteração de Status
              </h3>
              <p className="text-gray-600 mb-6">
                Tem certeza que deseja alterar o status do ativo para &quot;
                {formatStatus(newStatus)}&quot;?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmStatusChange}
                  disabled={updateStatusMutation.isLoading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {updateStatusMutation.isLoading ? 'Alterando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDebtModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Registrar Nova Dívida / Custo
                </h3>
                <button
                  type="button"
                  onClick={() => setShowDebtModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="px-6 py-4">
                {debtErrors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-red-600 text-sm">{debtErrors.submit}</p>
                  </div>
                )}

                <form onSubmit={handleSubmitDebt} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome do Credor *
                    </label>
                    <input
                      type="text"
                      name="creditorName"
                      value={debtForm.creditorName}
                      onChange={handleDebtInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        debtErrors.creditorName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Ex: Banco, Construtora, Loja"
                    />
                    {debtErrors.creditorName && (
                      <p className="text-red-500 text-xs mt-1">{debtErrors.creditorName}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Valor Original *
                      </label>
                      <div className="relative">
                        <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          name="originalAmount"
                          value={originalDebtAmountDisplay}
                          onChange={e =>
                            handleDebtCurrencyChange('originalAmount', e.target.value)
                          }
                          className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                            debtErrors.originalAmount ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="R$ 0,00"
                        />
                      </div>
                      {debtErrors.originalAmount && (
                        <p className="text-red-500 text-xs mt-1">
                          {debtErrors.originalAmount}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Saldo Devedor *
                      </label>
                      <div className="relative">
                        <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          name="currentAmount"
                          value={currentDebtAmountDisplay}
                          onChange={e =>
                            handleDebtCurrencyChange('currentAmount', e.target.value)
                          }
                          className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                            debtErrors.currentAmount ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="R$ 0,00"
                        />
                      </div>
                      {debtErrors.currentAmount && (
                        <p className="text-red-500 text-xs mt-1">
                          {debtErrors.currentAmount}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Taxa de Juros (% ao mês)
                      </label>
                      <input
                        type="number"
                        name="interestRate"
                        value={debtForm.interestRate}
                        onChange={handleDebtInputChange}
                        min={0}
                        max={100}
                        step={0.01}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="0,00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Vencimento *
                      </label>
                      <input
                        type="date"
                        name="dueDate"
                        value={debtForm.dueDate}
                        onChange={handleDebtInputChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                          debtErrors.dueDate ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {debtErrors.dueDate && (
                        <p className="text-red-500 text-xs mt-1">{debtErrors.dueDate}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        name="status"
                        value={debtForm.status}
                        onChange={handleDebtInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="active">Ativa</option>
                        <option value="overdue">Em Atraso</option>
                        <option value="renegotiated">Renegociada</option>
                        <option value="paid">Paga</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Método de Pagamento
                      </label>
                      <input
                        type="text"
                        name="paymentMethod"
                        value={debtForm.paymentMethod}
                        onChange={handleDebtInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Ex: Boleto, PIX, Débito em conta"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Número de Parcelas
                      </label>
                      <input
                        type="number"
                        name="installments"
                        value={debtForm.installments}
                        onChange={handleDebtInputChange}
                        min={1}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Parcela Atual
                      </label>
                      <input
                        type="number"
                        name="currentInstallment"
                        value={debtForm.currentInstallment}
                        onChange={handleDebtInputChange}
                        min={1}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Descrição
                    </label>
                    <textarea
                      name="description"
                      rows={3}
                      value={debtForm.description}
                      onChange={handleDebtInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Descreva a finalidade da dívida ou custo"
                    />
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={createDebtForAssetMutation.isLoading}
                      className="w-full inline-flex justify-center items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {createDebtForAssetMutation.isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Salvando...
                        </>
                      ) : (
                        'Registrar Dívida / Custo'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal da Galeria de Imagens */}
        {showImageGallery && asset.attachments && (
          <ImageGallery
            images={asset.attachments.filter(att => att.type === 'image')}
            initialIndex={galleryStartIndex}
            onClose={() => setShowImageGallery(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AssetDetailNew;
